import Model, { attr } from '@warp-drive/legacy/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { ChartType } from '../types/chart';

type DateGroupingInterval = 'day' | 'week' | 'month' | 'year';
type AggregationSortOrder = 'count-desc' | 'count-asc' | 'alpha-asc' | 'alpha-desc';

interface RangeBucket {
  key: string;
  from?: number;
  to?: number;
}

interface ChartSettings {
  selectedType?: ChartType;
  selectedField?: string;
  selectedDataset?: string;
  selectedTimeRange?: string;
  dateGrouping?: DateGroupingInterval;
  sumField?: string;
  summaryTitle?: string;
  aggregationType?: 'terms' | 'range';
  ranges?: RangeBucket[];
  sortOrder?: AggregationSortOrder;
}

interface AggregationMetric {
  type: 'count' | 'avg' | 'sum' | 'min' | 'max';
  field?: string;
  label: string;
}

interface AggregationTableSettings {
  groupByField: string;
  groupByLabel?: string;
  metrics: AggregationMetric[];
  size?: number;
}

interface TableSettings {
  showIndividualApplications?: boolean;
  selectedColumns?: string | string[];
  columnOrder?: string[];
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

interface SearchConfigFilters {
  selectedField?: string;
  selectedDataset?: string;
  selectedTimeRange?: string;
  applicationStates?: string[];
  requiredExistsFields?: string[];
  [key: string]: unknown;
}

interface SearchConfig {
  urlParams?: Record<string, string | string[]>;
  openSearchQuery?: Record<string, unknown>;
  viewType?: 'table' | 'chart' | 'aggregation_table';
  filters?: SearchConfigFilters;
  tableSettings?: TableSettings;
  chartSettings?: ChartSettings;
  aggregationTableSettings?: AggregationTableSettings;
  exportSettings?: Record<string, unknown>;

  // Chart builder specific properties (for backward compatibility)
  selectedDataset?: string;
  selectedGroupByField?: string;
  selectedChartType?: ChartType;
  selectedTimeRange?: string;
  selectedDateGrouping?: DateGroupingInterval;

  // Chart metadata for new format
  metadata?: ChartMetadata;

  [key: string]: unknown;
}

interface ChartMetadata {
  dataset?: string;
  groupByField?: string;
  chartType?: ChartType;
  timeRange?: string;
  dateGrouping?: DateGroupingInterval;
  sortOrder?: AggregationSortOrder;
}

type ReportScope = 'admin_only' | 'partner_admin';

interface ReportMetadata {
  serializedQuery?: string;
  chartType?: ChartType;
  aggregationType?: string;

  // Chart builder specific metadata
  dataset?: string;
  groupByField?: string;
  timeRange?: string;
  dateGrouping?: DateGroupingInterval;

  // Publish state metadata
  scope?: ReportScope;
  publishedAt?: string;
  publishedCompanyId?: string;

  // Archive state
  archivedAt?: string;

  [key: string]: unknown;
}

export default class Report extends Model {
  @attr declare name: string;
  @attr declare description: string;
  @attr declare reportType: string;

  // Store the raw search builder data as JSON (maps to search_config column)
  @attr declare searchConfig: SearchConfig;

  // Store any additional metadata including serialized query
  @attr declare metadata: ReportMetadata;

  // Partner visibility control - simplified array approach
  @attr declare partnerAccessConfig: number[];

  @attr declare visibilityLevel: string;
  @attr declare createdBy: number | undefined;
  @attr declare createdAt: string;
  @attr declare updatedAt: string;

  // Computed properties
  get isPrivate(): boolean {
    return this.visibilityLevel === 'private';
  }

  get isPartnerShared(): boolean {
    return this.visibilityLevel === 'partner';
  }

  get isGlobal(): boolean {
    return this.visibilityLevel === 'global';
  }

  get isChartBuilder(): boolean {
    return this.reportType === 'chart_builder';
  }

  get isTableBuilder(): boolean {
    return this.reportType === 'table_builder';
  }

  get isAggregationTable(): boolean {
    return this.reportType === 'aggregation_table';
  }

  // Scope (published state) properties
  get scope(): ReportScope {
    return this.metadata?.scope === 'partner_admin' ? 'partner_admin' : 'admin_only';
  }

  get isPublished(): boolean {
    return this.scope === 'partner_admin';
  }

  get isUnpublished(): boolean {
    return !this.isPublished;
  }

  get publishedAt(): string | undefined {
    return this.metadata?.publishedAt;
  }

  get publishedCompanyId(): string | undefined {
    return this.metadata?.publishedCompanyId;
  }

  get scopeLabel(): string {
    return this.isPublished ? 'Published' : 'Unpublished';
  }

  get isArchived(): boolean {
    return Boolean(this.metadata?.archivedAt);
  }

  get archivedAt(): string | undefined {
    return this.metadata?.archivedAt;
  }

  // Partner access helpers for simplified array structure
  get isVisibleToAllPartners(): boolean {
    // Only applies when visibility is 'partner'
    return (
      this.isPartnerShared && (!this.partnerAccessConfig || this.partnerAccessConfig.length === 0)
    );
  }

  get partnerIncludeList(): number[] {
    // Only applies when visibility is 'partner'
    if (!this.isPartnerShared) return [];
    return this.partnerAccessConfig?.filter((id) => id > 0) || [];
  }

  get partnerExcludeList(): number[] {
    // Only applies when visibility is 'partner'
    if (!this.isPartnerShared) return [];
    return this.partnerAccessConfig?.filter((id) => id < 0).map((id) => Math.abs(id)) || [];
  }

  get visibilityDescription(): string {
    if (this.isGlobal) return 'Visible to all users';
    if (this.isPrivate) return 'Private (only you)';

    if (this.isPartnerShared) {
      if (this.isVisibleToAllPartners) return 'Visible to all partners';

      const includeList = this.partnerIncludeList;
      const excludeList = this.partnerExcludeList;

      if (includeList.length > 0) {
        return `Visible to ${includeList.length} specific partner${includeList.length === 1 ? '' : 's'}`;
      }

      if (excludeList.length > 0) {
        return `Visible to all partners except ${excludeList.length}`;
      }
    }

    return 'Unknown visibility';
  }

  // Method to check if a specific company can see this report
  isVisibleToCompany(companyId: number): boolean {
    if (this.isGlobal) return true;
    if (this.isPrivate) return false; // Only creator can see private reports

    if (this.isPartnerShared) {
      if (this.isVisibleToAllPartners) return true;

      const includeList = this.partnerIncludeList;
      const excludeList = this.partnerExcludeList;

      if (includeList.length > 0) {
        return includeList.includes(companyId);
      }

      if (excludeList.length > 0) {
        return !excludeList.includes(companyId);
      }
    }

    return false;
  }

  // Helper methods
  get hasSerializedQuery(): boolean {
    return Boolean(this.metadata?.serializedQuery);
  }

  get serializedQuery(): string | undefined {
    return this.metadata?.serializedQuery;
  }

  get displayName(): string {
    return this.name;
  }

  get formattedCreatedAt(): string {
    if (!this.createdAt) return '';
    return new Date(this.createdAt).toLocaleDateString();
  }

  get formattedUpdatedAt(): string {
    if (!this.updatedAt) return '';
    return new Date(this.updatedAt).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  get visibilityLabel(): string {
    switch (this.visibilityLevel) {
      case 'private':
        return 'Private';
      case 'partner':
        return 'TIO Admin Public';
      case 'global':
        return 'Global';
      default:
        return 'Unknown';
    }
  }

  get reportTypeLabel(): string {
    switch (this.reportType) {
      case 'chart_builder':
        return 'Chart Builder';
      case 'table_builder':
        return 'Table Builder';
      case 'aggregation_table':
        return 'Aggregation Table';
      default:
        return 'Unknown';
    }
  }

  [Type] = 'report' as const;
}

export type {
  SearchConfig,
  SearchConfigFilters,
  ReportMetadata,
  ReportScope,
  ChartMetadata,
  ChartSettings,
  TableSettings,
  AggregationTableSettings,
  AggregationMetric,
  RangeBucket,
};
